/* ═══════════════════════════════════════════════════════
   sheetStyles — the one stylesheet both public sheets share.
   ═══════════════════════════════════════════════════════
   Adapted from the approved mockup (mockups/sheets-v2.html, the .sheet block).
   Every rule is scoped under .sheet so the Suite's own styles never leak in
   and ours never leak out (the admin Preview renders the same component).

   Two looks, picked by the snapshot's `style`:
     Normcore (default)  white paper, DM Sans 300 / DM Mono, black section bars
     Cool  .sheet[data-style="cool"]  paper #F2EDE0, ink #161614, Helvetica
           Neue, orange #E8551E on markers only, headings with a 7px square

   Layout follows the CONTAINER width, not the viewport: .sheet-wrap is an
   inline-size container, so the admin Preview (600px box) shows the page
   layout while a phone gets the phone layout. Print is white paper in both
   styles, letter with 0.5in 0.6in margins, nothing under 11px, phones and
   buttons hidden, the session log shown. */

export const SHEET_CSS = `
.sheet-wrap{container-type:inline-size;max-width:816px;margin:0 auto}
.sheet{
  --paper:#FFFFFF;--ink:#111111;--muted:#5E5E5E;--faint:#9A9A9A;--rule:#E4E4E4;--acc:#111111;--soft:#F4F3F1;
  --f:"DM Sans",system-ui,-apple-system,sans-serif;--m:"DM Mono","SF Mono",ui-monospace,monospace;
  --wb:300;--ws:500;--wd:300;
  box-sizing:border-box;margin:0;background:var(--paper);color:var(--ink);font-family:var(--f);font-weight:var(--wb);
  font-size:14px;line-height:1.5;padding:46px 54px 34px;box-shadow:0 1px 2px rgba(0,0,0,.07),0 10px 34px rgba(0,0,0,.09)}
.sheet[data-style="cool"]{
  --paper:#F2EDE0;--ink:#161614;--muted:#4A4A44;--faint:#7C7A70;--rule:#D9D2BE;--acc:#E8551E;--soft:#FFFDF6;
  --f:"Helvetica Neue",Helvetica,Arial,sans-serif;--m:"Helvetica Neue",Helvetica,Arial,sans-serif;
  --wb:400;--ws:700;--wd:700}
.sheet *{box-sizing:border-box}
.sheet h1,.sheet h3,.sheet p,.sheet ul{margin:0}
.sheet b,.sheet strong{font-weight:var(--ws)}
.sheet a{color:inherit}

.sheet .lh{display:flex;justify-content:space-between;align-items:flex-start;gap:16px}
.sheet .lh img{height:24px;width:auto;display:block;margin-top:3px}
.sheet .lh .meta{text-align:right;font-family:var(--m);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);line-height:1.7;white-space:nowrap}
.sheet .lh .meta .doc{color:var(--ink);font-size:12px;letter-spacing:.16em}
.sheet[data-style="cool"] .lh .meta{letter-spacing:.05em;font-size:11px}
.sheet[data-style="cool"] .lh .meta .doc{font-weight:700;font-size:13px;letter-spacing:.1em}
.sheet .r2{border:0;border-top:2px solid var(--ink);margin:16px 0 18px;height:0}
.sheet .conf{font-size:10.5px;color:var(--faint);font-style:italic;margin:-8px 0 14px}
.sheet[data-style="cool"] .conf{font-style:normal}
.sheet .date{font-size:34px;line-height:1.08;font-weight:var(--wd);letter-spacing:-.01em;margin:0}
.sheet[data-style="cool"] .date{font-size:40px;letter-spacing:-.03em}

.sheet .tiles{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));margin:16px 0 14px;border-top:1px solid var(--rule);border-bottom:1px solid var(--rule)}
.sheet .tiles.two{grid-template-columns:repeat(2,minmax(0,1fr))}
.sheet .tiles.one{grid-template-columns:minmax(0,1fr)}
.sheet .tile{padding:10px 12px 11px 0;min-width:0}
.sheet .tile .k{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.sheet .tile+.tile{border-left:1px solid var(--rule);padding-left:14px}
.sheet .k{font-family:var(--m);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint);line-height:1.3}
.sheet[data-style="cool"] .k{letter-spacing:.07em;font-weight:700;font-size:10px}
.sheet .tile .v{font-size:25px;line-height:1.2;font-weight:var(--wd);font-variant-numeric:tabular-nums;margin-top:3px;white-space:nowrap}
.sheet .tile .v.txt{font-size:16px;white-space:normal;padding-top:4px}
.sheet[data-style="cool"] .tile.hot .v{color:var(--acc)}
.sheet .where{display:flex;flex-wrap:wrap;gap:4px 12px;align-items:baseline;font-size:15px}
.sheet .where .lnk{font-family:var(--m);font-size:11px;letter-spacing:.08em;text-transform:uppercase;border-bottom:1px solid var(--ink);text-decoration:none}
.sheet[data-style="cool"] .where .lnk{letter-spacing:.03em;font-weight:700;font-size:12px}
.sheet .also{margin-top:10px;font-size:13px;color:var(--muted)}
.sheet .mine{margin-top:16px;background:var(--soft);border:1px solid var(--rule);border-left:3px solid var(--ink);padding:10px 14px}
.sheet[data-style="cool"] .mine{border-left-color:var(--acc)}
.sheet .mine .k{margin-bottom:2px}

.sheet .sec{margin-top:24px;min-width:0}
.sheet .sec h3{margin:0 0 6px;font:400 10px/1 var(--m);letter-spacing:.16em;text-transform:uppercase;background:#111;color:#fff;padding:7px 10px}
.sheet[data-style="cool"] .sec h3{background:none;color:var(--ink);font:700 12px/1.2 var(--f);letter-spacing:.07em;border-top:2px solid var(--ink);padding:9px 0 2px}
.sheet[data-style="cool"] .sec h3::before{content:"";display:inline-block;width:7px;height:7px;background:var(--acc);margin-right:8px;vertical-align:1px}
.sheet .g2{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0 34px}

.sheet ul.f{list-style:none;margin:0;padding:0}
.sheet ul.f li{position:relative;padding:5px 0 5px 16px;border-bottom:1px solid var(--rule);white-space:pre-line}
.sheet ul.f li:last-child{border-bottom:0}
.sheet ul.f li::before{content:"";position:absolute;left:1px;top:13px;width:5px;height:5px;background:var(--ink)}
.sheet[data-style="cool"] ul.f li::before{background:var(--acc)}
.sheet ul.c{list-style:none;margin:0;padding:0}
.sheet ul.c li{display:flex;gap:10px;align-items:flex-start;padding:5px 0;border-bottom:1px solid var(--rule)}
.sheet ul.c li:last-child{border-bottom:0}
.sheet ul.c li.done span.t{color:var(--muted);text-decoration:line-through}
.sheet .bx{flex:none;width:13px;height:13px;border:1.5px solid var(--ink);border-radius:2px;margin-top:4px}
.sheet .bx[role="checkbox"]{cursor:pointer}
.sheet .bx.on{background:var(--ink)}

.sheet .row{display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) auto;gap:4px 14px;align-items:baseline;padding:6px 0;border-bottom:1px solid var(--rule)}
.sheet .row:last-child{border-bottom:0}
.sheet .row .r{color:var(--muted)}
.sheet .row .t{font-family:var(--m);font-size:13px;font-variant-numeric:tabular-nums;white-space:nowrap}
.sheet[data-style="cool"] .row .t{font-weight:700}
.sheet .ph{display:block;font-family:var(--m);font-size:12px;color:var(--muted);white-space:nowrap;font-weight:400}
.sheet .off{margin-top:6px;font-size:13px;color:var(--muted)}
.sheet .kv{display:grid;grid-template-columns:auto minmax(0,1fr);gap:4px 14px;align-items:baseline}
.sheet .kv .k{padding-top:2px}

.sheet .pl{display:grid;grid-template-columns:30px minmax(0,1.25fr) minmax(0,.8fr) minmax(0,1.7fr);gap:2px 14px;padding:7px 0;border-bottom:1px solid var(--rule);align-items:baseline}
.sheet .pl:last-child{border-bottom:0}
.sheet .pl .n{font-family:var(--m);font-size:12px;color:var(--faint)}
.sheet[data-style="cool"] .pl .n{color:var(--acc);font-weight:700}
.sheet .pl .g{font-family:var(--m);font-size:12px;color:var(--muted)}
.sheet[data-style="cool"] .pl .g{font-size:12.5px}
.sheet .pl .nt{color:var(--muted);font-size:13.5px}
.sheet .pl.hd{padding:2px 0 4px}
.sheet .pl.hd span{font-family:var(--m);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint)}
.sheet[data-style="cool"] .pl.hd span{letter-spacing:.07em;font-weight:700;font-size:10px}

.sheet .dl{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.3fr) minmax(0,.8fr) minmax(0,.8fr);gap:2px 14px;padding:7px 0;border-bottom:1px solid var(--rule);align-items:baseline}
.sheet .dl.three{grid-template-columns:minmax(0,2.3fr) minmax(0,.8fr) minmax(0,.8fr)}
.sheet .dl:last-child{border-bottom:0}
.sheet .dl.hd span{font-family:var(--m);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint)}
.sheet[data-style="cool"] .dl.hd span{letter-spacing:.07em;font-weight:700;font-size:10px}
.sheet .dl .s{color:var(--muted);font-size:13px;white-space:pre-line}

.sheet .log{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px 18px;margin-top:4px}
.sheet .log div{border-bottom:1px solid var(--ink);padding-top:18px;font-family:var(--m);font-size:9.5px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint)}
.sheet .log .wide{grid-column:1/-1}
.sheet[data-style="cool"] .log div{letter-spacing:.07em;font-weight:700;font-size:10px}

.sheet .foot{margin-top:26px;padding-top:10px;border-top:1px solid var(--rule);display:flex;justify-content:space-between;gap:12px;font-size:10.5px;color:var(--faint)}
.sheet .foot button{font:inherit;color:inherit;background:none;border:0;padding:0;cursor:pointer;text-decoration:underline}
.sheet .ask,.sheet .share{margin-top:22px;border:1px dashed var(--rule);border-radius:6px;padding:12px 14px;font-size:13px;color:var(--muted)}
.sheet .ask{text-align:center}
.sheet .share .btn{margin-top:8px}
.sheet .share .said{margin-top:6px;color:var(--ink);word-break:break-all}

/* session sheet bits */
.sheet .addr{font-size:16px}
.sheet .btns{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0 4px}
.sheet .btn{display:inline-block;padding:9px 14px;border:1px solid var(--ink);border-radius:6px;font-size:13.5px;text-decoration:none;color:var(--ink);background:transparent;font-weight:var(--wb);font-family:inherit;line-height:1.3;cursor:pointer}
.sheet .btn.pri{background:var(--ink);color:var(--paper)}
.sheet[data-style="cool"] .btn{border-width:1.5px;font-weight:700}
.sheet .who{display:grid;grid-template-columns:minmax(0,1fr) auto auto;gap:4px 10px;align-items:center;padding:8px 0;border-bottom:1px solid var(--rule)}
.sheet .who:last-child{border-bottom:0}
.sheet .who .r{display:block;color:var(--muted);font-size:13px}
.sheet .who .num{font-family:var(--m);font-size:13.5px;white-space:nowrap}
.sheet .who .acts{display:flex;gap:6px}
.sheet .mini{padding:5px 10px;border:1px solid var(--ink);border-radius:6px;font-size:12.5px;text-decoration:none;color:var(--ink)}
.sheet[data-style="cool"] .mini{border-width:1.5px;font-weight:700}

/* phone widths (container) */
@container (max-width:560px){
  .sheet{padding:22px 18px 26px;box-shadow:none}
  .sheet .lh img{height:19px}
  .sheet .lh .meta{font-size:9px}
  .sheet .lh .meta .doc{font-size:11px}
  .sheet .date{font-size:27px}
  .sheet[data-style="cool"] .date{font-size:31px}
  .sheet .tile{padding:8px 8px 9px 0}
  .sheet .tile+.tile{padding-left:9px}
  .sheet .tile .v{font-size:18px}
  .sheet .tile .v.txt{font-size:14px}
  .sheet .g2{grid-template-columns:minmax(0,1fr)}
  .sheet .row{grid-template-columns:minmax(0,1fr) auto}
  .sheet .row .r{grid-column:1;grid-row:2;font-size:13px}
  .sheet .row .t{grid-column:2;grid-row:1;align-self:start}
  .sheet .pl{grid-template-columns:24px minmax(0,1fr) auto}
  .sheet .pl .nt{grid-column:2/-1}
  .sheet .pl.hd{display:none}
  .sheet .dl,.sheet .dl.three{grid-template-columns:minmax(0,1fr) auto}
  .sheet .dl .s1{grid-column:1/-1}
  .sheet .dl.hd{display:none}
  .sheet .who{grid-template-columns:minmax(0,1fr) auto}
  .sheet .who .num{grid-column:1;grid-row:2}
  .sheet .who .acts{grid-column:2;grid-row:1/3}
}
.sheet .printonly{display:none}

/* Print. The Cool selectors above are more specific than a bare .sheet
   rule, so every override here names both looks: white paper and the 11px
   floor must win in either style. */
@media print{
  @page{size:letter;margin:.5in .6in}
  .sheet-wrap{max-width:none}
  .sheet,.sheet[data-style="cool"]{--paper:#fff;background:#fff;box-shadow:none;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sheet .printonly{display:block}
  .sheet .ph,.sheet .mine,.sheet .btns,.sheet .who .acts,.sheet .share,.sheet .ask,.sheet .foot button{display:none}
  .sheet .lh .meta,.sheet[data-style="cool"] .lh .meta{font-size:11px}
  .sheet .lh .meta .doc,.sheet[data-style="cool"] .lh .meta .doc{font-size:12px}
  .sheet .conf,.sheet .foot{font-size:11px}
  .sheet .k,.sheet[data-style="cool"] .k,.sheet .sec h3,.sheet .pl.hd span,.sheet[data-style="cool"] .pl.hd span,.sheet .dl.hd span,.sheet[data-style="cool"] .dl.hd span,.sheet .log div,.sheet[data-style="cool"] .log div{font-size:11px}
  .sheet .who{grid-template-columns:minmax(0,1fr) auto}
  .sheet .tiles,.sheet .row,.sheet .pl,.sheet .dl,.sheet .who,.sheet .kv,.sheet ul.f li,.sheet ul.c li,.sheet .log{break-inside:avoid}
  .sheet .sec h3{break-after:avoid;break-inside:avoid}
  .sheet .g2,.sheet .sec{break-inside:auto}
}
`;
